import { useState, useMemo } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useCarpetCatalog,
  useAddCarpetCatalogItem,
  useUpdateCarpetCatalogItem,
  useDeleteCarpetCatalogItem,
} from '@/hooks/useCarpetCatalog';
import type { CarpetPricingCatalogItem, CarpetPricingCategory } from '@jobos/shared';
import { CARPET_PRICING_CATEGORIES } from '@jobos/shared';

const CATEGORY_LABELS: Record<CarpetPricingCategory, string> = {
  service_tier: 'Service Tier',
  stairs: 'Stairs',
  hallways: 'Hallways & Landings',
  rooms: 'Rooms',
  extras: 'Extras',
  surcharges: 'Surcharges',
};

interface GroupSectionProps {
  category: CarpetPricingCategory;
  items: CarpetPricingCatalogItem[];
}

function GroupSection({ category, items }: GroupSectionProps) {
  const updateMutation = useUpdateCarpetCatalogItem();
  const deleteMutation = useDeleteCarpetCatalogItem();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editCategory, setEditCategory] = useState<CarpetPricingCategory>(category);
  const [editRate, setEditRate] = useState('');
  const [editPricingMode, setEditPricingMode] = useState<'qty_rate' | 'amount'>('qty_rate');
  const [editUnitLabel, setEditUnitLabel] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const startEdit = (item: CarpetPricingCatalogItem) => {
    setEditingId(item.id);
    setEditLabel(item.label);
    setEditCategory(item.category);
    setEditRate(Number(item.defaultRate).toFixed(2));
    setEditPricingMode(item.pricingMode);
    setEditUnitLabel(item.unitLabel ?? '');
  };

  const saveEdit = () => {
    if (!editingId) return;
    const trimmed = editLabel.trim();
    const parsed = parseFloat(editRate);
    if (!trimmed || isNaN(parsed) || parsed < 0) return;
    updateMutation.mutate({
      id: editingId,
      label: trimmed,
      category: editCategory,
      defaultRate: parsed,
      pricingMode: editPricingMode,
      unitLabel: editPricingMode === 'amount' ? null : (editUnitLabel.trim() || null),
    });
    setEditingId(null);
  };

  const cancelEdit = () => setEditingId(null);

  const confirmDelete = (id: string) => {
    deleteMutation.mutate(id);
    setDeletingId(null);
  };

  return (
    <div>
      <div className="px-4 py-2 bg-gray-100 border-b">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {CATEGORY_LABELS[category]}
        </span>
      </div>
      {items.map((item) => (
        <div
          key={item.id}
          className="flex items-center justify-between px-4 py-2.5 border-b hover:bg-gray-50 group"
        >
          {editingId === item.id ? (
            <div className="flex items-center gap-2 flex-1 flex-wrap">
              <Input
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveEdit();
                  if (e.key === 'Escape') cancelEdit();
                }}
                className="max-w-56 h-7 text-sm"
                autoFocus
              />
              <select
                value={editCategory}
                onChange={(e) =>
                  setEditCategory(e.target.value as CarpetPricingCategory)
                }
                className="h-7 text-sm rounded-md border border-gray-300 px-2 bg-white"
              >
                {CARPET_PRICING_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {CATEGORY_LABELS[cat]}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">$</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editRate}
                  onChange={(e) => setEditRate(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveEdit();
                    if (e.key === 'Escape') cancelEdit();
                  }}
                  className="w-24 h-7 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
              <select
                value={editPricingMode}
                onChange={(e) => setEditPricingMode(e.target.value as 'qty_rate' | 'amount')}
                className="h-7 text-sm rounded-md border border-gray-300 px-2 bg-white"
              >
                <option value="qty_rate">Qty × Rate</option>
                <option value="amount">Flat amount</option>
              </select>
              {editPricingMode === 'qty_rate' && (
                <Input
                  value={editUnitLabel}
                  onChange={(e) => setEditUnitLabel(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveEdit();
                    if (e.key === 'Escape') cancelEdit();
                  }}
                  maxLength={32}
                  placeholder="unit (room, stair, hr)"
                  className="max-w-40 h-7 text-sm"
                />
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={saveEdit}
                className="h-6 w-6 p-0"
              >
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={cancelEdit}
                className="h-6 w-6 p-0"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : deletingId === item.id ? (
            <div className="flex items-center gap-2 flex-1">
              <span className="text-xs text-red-600">Delete "{item.label}"?</span>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => confirmDelete(item.id)}
                disabled={deleteMutation.isPending}
                className="h-6 text-xs"
              >
                Confirm
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDeletingId(null)}
                className="h-6 text-xs"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-900">{item.label}</span>
                <span className="text-sm text-gray-500">
                  ${Number(item.defaultRate).toFixed(2)}
                </span>
                {item.pricingMode === 'qty_rate' && item.unitLabel && (
                  <span className="text-xs text-gray-400">per {item.unitLabel}</span>
                )}
              </div>
              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => startEdit(item)}
                  className="h-6 w-6 p-0"
                >
                  <Pencil className="h-3 w-3 text-gray-500" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDeletingId(item.id)}
                  className="h-6 w-6 p-0"
                >
                  <Trash2 className="h-3 w-3 text-gray-500" />
                </Button>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

interface AddFormProps {
  defaultCategory: CarpetPricingCategory;
}

function AddForm({ defaultCategory }: AddFormProps) {
  const addMutation = useAddCarpetCatalogItem();

  const [label, setLabel] = useState('');
  const [category, setCategory] = useState<CarpetPricingCategory>(defaultCategory);
  const [rate, setRate] = useState('');
  const [pricingMode, setPricingMode] = useState<'qty_rate' | 'amount'>('qty_rate');
  const [unitLabel, setUnitLabel] = useState('');

  const handleAdd = () => {
    const trimmed = label.trim();
    const parsed = parseFloat(rate);
    if (!trimmed || isNaN(parsed) || parsed < 0) return;
    addMutation.mutate(
      {
        label: trimmed,
        category,
        defaultRate: parsed,
        pricingMode,
        unitLabel: pricingMode === 'amount' ? null : (unitLabel.trim() || null),
      },
      {
        onSuccess: () => {
          setLabel('');
          setRate('');
          setCategory(defaultCategory);
          setPricingMode('qty_rate');
          setUnitLabel('');
        },
      },
    );
  };

  return (
    <div className="flex items-center gap-2 p-4 flex-wrap bg-gray-50/50">
      <Input
        placeholder="Item name"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        className="max-w-56 h-7 text-sm"
      />
      <select
        value={category}
        onChange={(e) => setCategory(e.target.value as CarpetPricingCategory)}
        className="h-7 text-sm rounded-md border border-gray-300 px-2 bg-white"
      >
        {CARPET_PRICING_CATEGORIES.map((cat) => (
          <option key={cat} value={cat}>
            {CATEGORY_LABELS[cat]}
          </option>
        ))}
      </select>
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-500">$</span>
        <Input
          type="number"
          min="0"
          step="0.01"
          placeholder="0.00"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          className="w-24 h-7 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
      </div>
      <select
        value={pricingMode}
        onChange={(e) => setPricingMode(e.target.value as 'qty_rate' | 'amount')}
        className="h-7 text-sm rounded-md border border-gray-300 px-2 bg-white"
      >
        <option value="qty_rate">Qty × Rate</option>
        <option value="amount">Flat amount</option>
      </select>
      {pricingMode === 'qty_rate' && (
        <Input
          placeholder="unit (room, stair, hr)"
          value={unitLabel}
          onChange={(e) => setUnitLabel(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          maxLength={32}
          className="max-w-40 h-7 text-sm"
        />
      )}
      <Button
        onClick={handleAdd}
        disabled={
          !label.trim() || !rate || isNaN(parseFloat(rate)) || addMutation.isPending
        }
        size="sm"
        variant="ghost"
        className="h-7 text-xs"
      >
        <Plus className="h-3.5 w-3.5 mr-1" />
        Add
      </Button>
    </div>
  );
}

export default function CarpetPricingSettings() {
  const { data: items = [], isLoading } = useCarpetCatalog();

  const grouped = useMemo(() => {
    const map = new Map<CarpetPricingCategory, CarpetPricingCatalogItem[]>();
    for (const cat of CARPET_PRICING_CATEGORIES) map.set(cat, []);
    for (const item of items) map.get(item.category)?.push(item);
    for (const cat of CARPET_PRICING_CATEGORIES) {
      map.get(cat)?.sort((a, b) => a.sortOrder - b.sortOrder);
    }
    return map;
  }, [items]);

  if (isLoading) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Carpet Cleaning Rate Card</h2>
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        Carpet Cleaning Rate Card
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        Service tiers, items, and surcharges available in the carpet breakdown editor.
      </p>

      <div className="border rounded-lg overflow-hidden divide-y">
        {CARPET_PRICING_CATEGORIES.map((cat) => {
          const catItems = grouped.get(cat) ?? [];
          if (catItems.length === 0) return null;
          return <GroupSection key={cat} category={cat} items={catItems} />;
        })}
        {items.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-gray-500">
            No catalog items yet. Add one below.
          </div>
        )}
        <AddForm defaultCategory="rooms" />
      </div>
    </div>
  );
}
